import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Download, FileText } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { downloadCaseFile } from "@/api/queries/cases";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StepStatusBadge, akteurLabel } from "@/components/ProcessBits";
import { formatBytes } from "./fileVersions";
import { entscheidKlasse, entscheidLabel, zitatRahmen } from "./decisionLabel";
import { StepTranscript } from "./StepTranscript";
import type { CaseStepFold, CaseStepsResult } from "@/types";

// #303 (UC-2): die Schritt-Sicht — „was bekam Schritt X, was lieferte er, wer hat entschieden".
//
// Der Verlauf daneben zeigt die Chronik; hier steht dieselbe Wahrheit pro Schritt gefaltet.
// Genau das war Arbens Wunsch (2026-08-27): „ich will in jedem Step sehen, was Input/Output
// war" — statt dass der Leser die Chronik selbst zusammensetzt.

/** Anker-Id einer Schritt-Karte — der Graph springt darauf. */
export function stepAnkerId(stepId: string): string {
  return `schritt-${stepId}`;
}

/**
 * Fremdformat-Härtung: was der Server schickt, ist für die Oberfläche eine ANNAHME.
 *
 * ⚠ Nicht theoretisch — genau daran ist der erste Bau gescheitert: eine Antwort ohne
 * `fragen`-Liste (Teil-Antwort, älterer Master, ein Endpoint, der zufällig auf dieselbe
 * Pfad-Präfix passt) riss die GANZE Lauf-Seite ab. Eine Anzeige darf an einem fehlenden
 * Beiwerk-Feld nicht sterben; sie zeigt dann eben nichts (Regel wie bei `darfVorgang`).
 */
function liste<T>(v: readonly T[] | undefined | null): readonly T[] {
  return Array.isArray(v) ? v : [];
}

/** Die Schritt-Liste in einer Form, auf die sich Timeline UND Ablauf-Bild verlassen können. */
export function alsSchrittListe(daten: CaseStepsResult | undefined): CaseStepFold[] {
  if (!daten || !Array.isArray(daten.steps)) return [];
  return daten.steps
    .filter((s): s is CaseStepFold => !!s && typeof s === "object" && typeof s.id === "string")
    .map((s) => ({
      ...s,
      nummer: typeof s.nummer === "number" ? s.nummer : 0,
      // #303 (Review L3): ein FEHLENDER Zustand ergab ein leeres Abzeichen — ein Kästchen
      // ohne Wort, das nach einem Darstellungsfehler aussieht. „offen" ist dafür die
      // zurückhaltendste Aussage (nichts ist passiert). Ein unbekannter, aber gefüllter
      // Wert wird dagegen durchgereicht: er ist die Aussage eines neueren Masters.
      // (Der Typ sagt „einer von sechs" — der Server sagt es nicht, deshalb die Prüfung.)
      status: typeof s.status === "string" && (s.status as string) !== "" ? s.status : "pending",
      fragen: [...liste(s.fragen)],
      entscheidungen: [...liste(s.entscheidungen)],
      toolPermissions: [...liste(s.toolPermissions)],
      eskalationen: [...liste(s.eskalationen)],
      redispatches: [...liste(s.redispatches)],
      dateien: [...liste(s.dateien)],
      hinweise: [...liste(s.hinweise)],
    }));
}

function zeit(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString("de-CH") : "—";
}

/** `user:u1` → `u1`; alles andere unverändert (Agenten/System heissen anders). */
function person(actor: string | null): string {
  if (!actor) return "unbekannt";
  return actor.startsWith("user:") ? actor.slice(5) : actor;
}

function Werteliste({ werte, testId }: { werte: Record<string, unknown>; testId: string }) {
  const eintraege = Object.entries(werte);
  if (eintraege.length === 0) return null;
  return (
    <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-2" data-testid={testId}>
      {eintraege.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <dt className="font-mono text-[11px] text-text3">{k}</dt>
          <dd className="break-words text-[12.5px] text-text">
            {typeof v === "object" && v !== null ? JSON.stringify(v) : String(v)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function Block({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-text3">{titel}</div>
      {children}
    </div>
  );
}

/**
 * Der Ausgang einer Werkzeug-Freigabe (#302 UC-6) in Klartext. `timeout`/`undeliverable`
 * sind KEINE Ablehnungen — sie heissen „niemand hat entschieden" bzw. „die Frage kam nie
 * an", und ein pauschales „abgelehnt" verwischte genau den Unterschied, der einen Vorgang
 * erklärt.
 */
function freigabeAusgang(entscheid: string | null): string {
  switch (entscheid) {
    case "allow":
      return "erlaubt";
    case "deny":
      return "abgelehnt";
    case "timeout":
      return "Frist abgelaufen — niemand hat entschieden";
    case "undeliverable":
      return "nicht zustellbar";
    case null:
      return "noch offen";
    default:
      return entscheid;
  }
}

function SchrittKarte({ caseId, step }: { caseId: string; step: CaseStepFold }) {
  const [offen, setOffen] = useState(false);
  const { session } = useAuth();

  const lade = (attachmentId: string, filename: string) => {
    if (!session) return;
    void downloadCaseFile(session.accessToken, caseId, attachmentId, filename).catch(() =>
      toast.error("Datei konnte nicht geladen werden"),
    );
  };

  return (
    <div
      id={stepAnkerId(step.id)}
      className="scroll-mt-24 rounded-lg border border-border bg-surface px-4 py-3"
      data-testid="step-card"
      data-step-id={step.id}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[11.5px] text-text3">{step.nummer}.</span>
        <span className="text-[13.5px] font-semibold text-text">{step.name ?? step.id}</span>
        {step.name && <span className="font-mono text-[11px] text-text3">{step.id}</span>}
        <StepStatusBadge status={step.status} />
        {akteurLabel(step.akteur) && (
          <Badge variant="outline">{akteurLabel(step.akteur)}</Badge>
        )}
      </div>

      <div className="mt-1 flex flex-wrap gap-x-4 text-[11.5px] text-text3">
        <span>Begonnen: {zeit(step.begonnenAm)}</span>
        <span>Beendet: {zeit(step.beendetAm)}</span>
        {step.zustellungen > 0 && (
          <span data-testid="step-zustellungen">
            {step.zustellungen} {step.zustellungen === 1 ? "Zustellung" : "Zustellungen"} an den Agenten
          </span>
        )}
      </div>

      <div className="mt-3 space-y-3">
        {step.input && Object.keys(step.input).length > 0 && (
          <Block titel="Bekommen">
            <Werteliste werte={step.input} testId="step-input" />
            {step.inputUnvollstaendig && (
              <p className="text-[11.5px] text-warn">
                Der Feldstand beim Eintritt war nachweislich unvollständig.
              </p>
            )}
          </Block>
        )}

        {(step.output || step.outputGekuerzt) && (
          <Block titel="Geliefert">
            {step.output && <Werteliste werte={step.output} testId="step-output" />}
            {step.outputGekuerzt && (
              <p className="text-[11.5px] text-warn" data-testid="step-output-gekuerzt">
                Die Werte waren zu gross zum Mitschreiben — geliefert wurden:{" "}
                {(step.outputKeys ?? []).join(", ") || "unbekannte Felder"}.
              </p>
            )}
          </Block>
        )}

        {step.fragen.length > 0 && (
          <Block titel="Rückfragen">
            <div className="space-y-1.5" data-testid="step-fragen">
              {step.fragen.map((f, i) => (
                <div key={i} className="text-[12.5px]">
                  <div className="text-text">{f.frage}</div>
                  <div className="text-[11.5px] text-text3">
                    gefragt {zeit(f.gestelltAm)}
                    {f.askRole ? ` · an Rolle ${f.askRole}` : " · an den Inhaber"}
                  </div>
                  {f.antwort !== null ? (
                    <div className="mt-0.5 border-l-2 border-accent-border pl-2 text-text2">
                      {f.antwort}
                      <span className="ml-1.5 text-[11.5px] text-text3">
                        — {person(f.antwortVon)}, {zeit(f.beantwortetAm)}
                      </span>
                    </div>
                  ) : (
                    <div className="mt-0.5 text-[11.5px] text-warn">noch offen</div>
                  )}
                </div>
              ))}
            </div>
          </Block>
        )}

        {step.entscheidungen.length > 0 && (
          <Block titel="Entscheidungen">
            <div className="space-y-1" data-testid="step-entscheidungen">
              {step.entscheidungen.map((e, i) => (
                <div key={i} className="text-[12.5px] text-text2">
                  {/* #324 (UC-2): Ausgang + Runde in EINEM Wort-Zug. Eine fehlende Runde
                      (Läufe von vor #323) lässt den Zusatz weg — nie „Runde undefined". */}
                  <span className={entscheidKlasse(e.entscheid)}>{entscheidLabel(e.entscheid, e.runde)}</span>{" "}
                  von {person(e.von)} · {zeit(e.am)}
                  {e.quorum !== null && e.quorum > 1 && (
                    <span className="ml-1.5 text-[11.5px] text-text3">
                      ({e.zustimmungen ?? "?"} von {e.quorum} Stimmen)
                    </span>
                  )}
                  {e.approverRole && (
                    <span className="ml-1.5 text-[11.5px] text-text3">Rolle {e.approverRole}</span>
                  )}
                  {/* Der Auftrag bzw. die Begründung als Zitat — ohne ihn stünde da „zur
                      Überarbeitung" und niemand wüsste, WAS zu tun war. Server-Text, von
                      React escapt; mehrzeilig bleibt mehrzeilig. */}
                  {typeof e.feedback === "string" && e.feedback.trim() !== "" && (
                    <div
                      // Der Rahmen folgt dem AUSGANG (Review L4): eine Ablehnungs-Begründung
                      // gelb zu rahmen läse sich wie „geht noch weiter".
                      className={`mt-0.5 whitespace-pre-wrap break-words border-l-2 pl-2 text-text2 ${zitatRahmen(e.entscheid)}`}
                      data-testid="step-entscheidung-feedback"
                    >
                      {e.feedback}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Block>
        )}

        {step.toolPermissions.length > 0 && (
          <Block titel="Werkzeug-Freigaben">
            <div className="space-y-1" data-testid="step-tool-permissions">
              {step.toolPermissions.map((t, i) => (
                <div key={i} className="text-[12.5px] text-text2">
                  <span className="font-mono text-[11.5px] text-text">{t.werkzeugText}</span>
                  <span className="mx-1.5 text-text3">→</span>
                  <span className={t.entscheid === "allow" ? "text-ok" : t.entscheid === "deny" ? "text-danger" : "text-warn"}>
                    {freigabeAusgang(t.entscheid)}
                  </span>
                  {t.von && <span className="ml-1.5">von {person(t.von)}</span>}
                  <span className="ml-1.5 text-[11.5px] text-text3">
                    {t.fallbackOwner
                      ? "gefragt wurde der Besitzer des Agenten"
                      : t.empfaengerRolle
                        ? `gefragt wurde Rolle ${t.empfaengerRolle}`
                        : ""}
                  </span>
                </div>
              ))}
            </div>
          </Block>
        )}

        {step.eskalationen.length > 0 && (
          <Block titel="Eskalationen">
            <div className="space-y-1" data-testid="step-eskalationen">
              {step.eskalationen.map((e, i) => (
                <div key={i} className="text-[12.5px] text-warn">
                  {e.grund}
                  {e.escalationRole && <span className="ml-1.5 text-text3">an Rolle {e.escalationRole}</span>}
                  <span className="ml-1.5 text-[11.5px] text-text3">{zeit(e.am)}</span>
                </div>
              ))}
            </div>
          </Block>
        )}

        {step.hinweise.length > 0 && (
          <Block titel="Hinweise">
            <div className="space-y-1" data-testid="step-hinweise">
              {step.hinweise.map((h, i) => (
                <div key={i} className="whitespace-pre-wrap break-words text-[12.5px] text-text2">
                  <span className={h.art === "blocked" ? "text-danger" : "text-text3"}>
                    {h.art === "blocked" ? "blockiert" : "übersprungen"}
                  </span>
                  {h.grund ? `: ${h.grund}` : ""}
                  <span className="ml-1.5 text-[11.5px] text-text3">{zeit(h.am)}</span>
                </div>
              ))}
            </div>
          </Block>
        )}

        {step.redispatches.length > 0 && (
          <Block titel="Neu angestossen">
            <div className="space-y-1" data-testid="step-redispatches">
              {step.redispatches.map((r, i) => (
                <div key={i} className="text-[12.5px] text-text2">
                  {r.ausloeser === "retry" ? "erneut versucht" : "erneut angestossen"} von {person(r.von)} ·{" "}
                  {zeit(r.am)}
                </div>
              ))}
            </div>
          </Block>
        )}

        {step.dateien.length > 0 && (
          <Block titel="Abgegebene Dateien">
            <div className="space-y-1" data-testid="step-dateien">
              {step.dateien.map((d) => (
                <div key={d.attachmentId} className="flex flex-wrap items-center gap-2 text-[12.5px]">
                  <FileText className="h-3.5 w-3.5 shrink-0 text-text3" />
                  <span className="min-w-0 break-all text-text">{d.filename}</span>
                  {d.version > 1 && <Badge variant="outline">Fassung {d.version}</Badge>}
                  <span className="text-[11.5px] text-text3">{formatBytes(d.size)}</span>
                  <Button size="sm" variant="ghost" onClick={() => lade(d.attachmentId, d.filename)}>
                    <Download className="h-3.5 w-3.5" />
                    Herunterladen
                  </Button>
                </div>
              ))}
            </div>
          </Block>
        )}

        <div>
          <button
            type="button"
            onClick={() => setOffen((o) => !o)}
            className="inline-flex items-center gap-1 text-[12px] text-accent hover:underline"
            data-testid="step-transcript-toggle"
          >
            {offen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Arbeitsprotokoll
          </button>
          {offen && (
            <div className="mt-2 rounded-md border border-border bg-surface-2 px-3 py-2">
              <StepTranscript caseId={caseId} stepId={step.id} offen={offen} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function StepTimeline({
  caseId,
  daten,
  schritte,
}: {
  caseId: string;
  daten: CaseStepsResult;
  /**
   * Die bereits gefaltete Liste (#303 Review L4). Die Seite braucht sie ohnehin für das
   * Ablauf-Bild und die Datei-Karte — sie hier ein zweites Mal zu bauen hiesse, dieselbe
   * Antwort in jedem Live-Takt doppelt durchzurechnen. Fehlt sie, faltet die Zeitleiste
   * selbst (die Komponente bleibt für sich benutzbar).
   */
  schritte?: readonly CaseStepFold[];
}) {
  const steps = schritte ?? alsSchrittListe(daten);
  return (
    <div className="space-y-3">
      {daten.specLesbar === false && (
        <p className="text-[12px] text-warn" data-testid="steps-spec-unlesbar">
          Die Vorlage dieses Laufs ist nicht abrufbar — Schritt-Namen, Rollen und der
          Eintritts-Feldstand fehlen deshalb. Der Ablauf selbst stimmt.
        </p>
      )}
      {daten.gekuerzt && (
        <p className="text-[12px] text-warn" data-testid="steps-gekuerzt">
          Sehr langer Vorgang — gefaltet wurde nur sein Anfang. Spätere Abgaben und
          Entscheidungen stehen im Verlauf weiter unten.
        </p>
      )}
      {steps.length === 0 ? (
        <p className="text-[12.5px] text-text3">Noch keine Schritte.</p>
      ) : (
        steps.map((s) => <SchrittKarte key={s.id} caseId={caseId} step={s} />)
      )}
    </div>
  );
}
